import {
    useCallback,
    useEffect, useRef, useState,
} from 'react';

import {getElementHeight} from 'shared/utils/dom';

interface ICachedElements {
    thead: HTMLElement;
    pinButton: HTMLElement;
    tableActions: HTMLElement;
    collapse: HTMLElement;
    tableReportContent: HTMLElement;
    innerTable: HTMLElement;
    tableContent: HTMLElement;
}

const COMPACT_MODE_TOP_OFFSET = 116;
const REGULAR_MODE_TOP_OFFSET = 56;
const COMPACT_HEADER_HEIGHT = 60;
const REGULAR_HEADER_HEIGHT = 80;
const TABLE_STICKY_WIDTH_BREAKPOINT_WITH_ASIDE = 1264;
const TABLE_STICKY_WIDTH_BREAKPOINT_NO_ASIDE = 1434;

export const useStickyTableHeader = (isAsideMenuEnabled = false, initialPinned = true) => {
    const [isTableHeaderPinned, setIsTableHeaderPinned] = useState(initialPinned);
    const tableRef = useRef<HTMLDivElement>(null);
    const cachedElementsRef = useRef<Partial<ICachedElements>>({});

    /**
     * Получает и кэширует DOM-элементы, связанные с таблицей.
     *
     * Функция ищет элементы внутри DOM-узла `tableRef.current` по заданным CSS-селекторам
     * (например, заголовок таблицы, кнопка закрепления, панель действий и т.д.).
     * Чтобы избежать повторных вызовов `querySelector`, найденные элементы сохраняются
     * в `cachedElementsRef`, и при последующих вызовах используются уже кэшированные значения.
     *
     * Это позволяет сократить количество DOM-операций и повысить производительность.
     */
    const getCachedElements = useCallback(() => {
        const table = tableRef.current;
        if (!table) return {};

        const cache = cachedElementsRef.current;
        const elementsData: Record<keyof ICachedElements, string> = {
            thead: '.ant-table-thead',
            pinButton: '.pin-button',
            tableActions: '.table-report-actions__wrapper',
            collapse: '.ant-collapse-item',
            tableReportContent: '.table-report__content',
            innerTable: 'table',
            tableContent: '.ant-table-content',
        };

        (Object.entries(elementsData) as [keyof ICachedElements, string][]).forEach(([key, selector]) => {
            if (!cache[key]) {
                cache[key] = table.querySelector(selector) as HTMLElement;
            }
        });

        return cache;
    }, [tableRef.current]);

    /**
     * Вычисляет значение вертикального отступа (`top`) для "липкого" заголовка таблицы.
     *
     * Отступ рассчитывается на основе текущей позиции прокрутки окна и суммарной высоты
     * элементов, расположенных выше таблицы (панель действий, блоки collapse),
     * а также режима отображения (компактный или обычный).
     *
     * @param scrollY - Текущая вертикальная прокрутка окна.
     * @param actionsHeight - Высота панели действий над таблицей.
     * @param collapseHeight - Высота свернутых блоков над таблицей.
     * @param isCompact - Признак компактного режима отображения.
     * @returns Числовое значение отступа `top` для закрепления заголовка.
     */
    const calculateStickyTopOffset = useCallback((
        scrollY: number,
        actionsHeight: number,
        collapseHeight: number,
        isCompact: boolean,
    ): number => {
        const offset = isCompact ? COMPACT_MODE_TOP_OFFSET : REGULAR_MODE_TOP_OFFSET;
        return Number((scrollY - actionsHeight - collapseHeight - offset).toFixed(1));
    }, []);

    /**
     * Применяет CSS-классы и inline-стили, необходимые для закрепления заголовка таблицы.
     *
     * Устанавливает значения:
     * - ширины заголовка с учётом коррекции,
     * - высоты в зависимости от режима (компактный/обычный),
     * - позицию `top`, соответствующую текущей прокрутке и layout.
     *
     * @param thead - DOM-элемент заголовка таблицы (`<thead>`).
     * @param pinButton - DOM-элемент кнопки закрепления заголовка (если есть).
     * @param theadWidth - Текущая ширина таблицы (контейнера с содержимым).
     * @param isCompact - Флаг, указывающий на компактный режим отображения.
     * @param stickyTopOffset - Значение отступа `top` в пикселях.
     */
    const applyStickyStyles = useCallback((
        thead: HTMLElement,
        pinButton: HTMLElement | undefined,
        theadWidth: number,
        isCompact: boolean,
        stickyTopOffset: number,
    ): void => {
        thead.classList.add('sticky-header');
        pinButton?.classList.add('pin-button_sticky');

        thead.style.setProperty('--thead-width', `${theadWidth}px`);
        thead.style.setProperty('--thead-height', `${isCompact ? COMPACT_HEADER_HEIGHT : REGULAR_HEADER_HEIGHT}px`);
        thead.style.top = `${stickyTopOffset}px`;
    }, []);

    /**
     * Удаляет CSS-классы и сбрасывает inline-стили, связанные с "липким" состоянием заголовка таблицы.
     *
     * Используется, когда заголовок должен вернуться в обычное (нефиксированное) положение.
     *
     * @param thead - DOM-элемент заголовка таблицы (`<thead>`).
     * @param pinButton - DOM-элемент кнопки закрепления заголовка (если есть).
     */
    const clearStickyStyles = useCallback((
        thead: HTMLElement,
        pinButton?: HTMLElement,
    ): void => {
        thead.classList.remove('sticky-header');
        pinButton?.classList.remove('pin-button_sticky');
    }, []);

    /**
     * Обновляет положение и стили заголовка таблицы при прокрутке страницы.
     *
     * Функция определяет, пересёк ли пользователь порог прокрутки, при котором заголовок таблицы
     * должен стать "липким" (sticky). Если да — добавляются соответствующие классы и inline-стили,
     * чтобы закрепить заголовок в верхней части экрана.
     *
     * Учитываются:
     * - смещения от элементов выше таблицы (панель действий, блоки collapse),
     * - текущая ширина таблицы (влияет на отступы и высоту заголовка),
     * - наличие бокового меню (`isAsideMenuEnabled`), которое влияет на брейкпоинты и поведение.
     *
     * Если заголовок не должен быть закреплён — классы и стили сбрасываются.
     */
    const syncTableHeaderPosition = useCallback(() => {
        const table = tableRef.current;
        if (!table) return;

        const {
            thead,
            pinButton,
            tableActions,
            collapse,
            innerTable,
            tableContent,
        } = getCachedElements();

        if (!thead || !innerTable || !tableContent) return;

        const tableTopOffset = innerTable.getBoundingClientRect().top + window.scrollY;
        const actionsHeight = getElementHeight(tableActions);
        const collapseHeight = getElementHeight(collapse);
        const theadHeight = getElementHeight(thead);
        const theadWidth = tableContent.clientWidth;

        const isScrolledPastTableTop = window.scrollY >= tableTopOffset - theadHeight;
        const widthBreakpoint = isAsideMenuEnabled
            ? TABLE_STICKY_WIDTH_BREAKPOINT_WITH_ASIDE
            : TABLE_STICKY_WIDTH_BREAKPOINT_NO_ASIDE;

        const isCompactMode = theadWidth >= widthBreakpoint;

        if (isScrolledPastTableTop && isTableHeaderPinned) {
            const stickyTopOffset = calculateStickyTopOffset(
                window.scrollY,
                actionsHeight,
                collapseHeight,
                isCompactMode,
            );

            applyStickyStyles(thead, pinButton, theadWidth, isCompactMode, stickyTopOffset);
        } else {
            clearStickyStyles(thead, pinButton);
        }
    }, [tableRef.current, isAsideMenuEnabled, isTableHeaderPinned, getCachedElements]);

    /**
     * useEffect hook для обработки прокрутки страницы, вычисление позиции скролла
     *  и добавления класса 'sticky-header' к заголовку таблицы.
     *
     * @function
     * @returns {void}
     */
    useEffect(() => {
        if (isTableHeaderPinned) {
            syncTableHeaderPosition();
        }

        let rafId: number | null = null;

        const handleScroll = () => {
            if (!isTableHeaderPinned) {
                return;
            }

            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                syncTableHeaderPosition();
            });
        };

        window.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            cachedElementsRef.current = {};
        };
    }, [isTableHeaderPinned, syncTableHeaderPosition]);

    /**
     * Эффект для наблюдения за изменением ширины таблицы и динамического обновления размера заголовка таблицы.
     *
     * @function
     * @returns {void}
     */
    useEffect(() => {
        if (!tableRef.current) {
            return () => {};
        }

        const resizeObserver = new ResizeObserver(() => {
            syncTableHeaderPosition();
        });

        resizeObserver.observe(tableRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    },
    [tableRef.current, syncTableHeaderPosition]);

    const handlePinButtonClick = () => {
        if (isTableHeaderPinned) {
            const table = tableRef.current;
            const tooltip = table?.querySelector('.ant-tooltip-inner') as HTMLDivElement;

            if (table && tooltip) {
                const mouseOverEvent = new MouseEvent('mouseover', {
                    bubbles: true,
                    cancelable: true,
                });

                const mouseOutEvent = new MouseEvent('mouseout', {
                    bubbles: true,
                    cancelable: true,
                });

                tooltip.dispatchEvent(mouseOverEvent);
                setTimeout(() => {
                    tooltip.dispatchEvent(mouseOutEvent);
                }, 10);
            }
        }

        setIsTableHeaderPinned(!isTableHeaderPinned);
    };

    return {
        tableRef,
        isTableHeaderPinned,
        handlePinButtonClick,
        setIsTableHeaderPinned,
    };
};
